import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import AeropuertosGrid from "./components/AeropuertosGrid";
import NewsletterSection from "../home/components/NewsletterSection";
import { aeropuertosRD } from "../../mocks/aeropuertos";
import SEO from "@/components/SEO";
import { AdCintillo } from "@/components/feature/SponsorAds";
import NewsletterPopup from "@/components/feature/NewsletterPopup";

export default function Aeropuertos() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("Todas");
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/actualidad?search=${encodeURIComponent(search.trim())}`);
    }
  };

  const categories = [
    "Todas",
    "Infraestructura",
    "Operaciones",
    "Modernización",
    "Certificaciones",
    "Tráfico Aéreo",
  ];

  return (
    <main className="min-h-screen bg-[#F4F7FB]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <SEO
        jsonLdSchemas={[
          {
            "@type": "WebPage",
            name: "Aeropuertos",
            description: "Infraestructura aeroportuaria, noticias y datos del sector aéreo en República Dominicana y el Caribe.",
            url: 'https://aviacioninsightrd.com/aeropuertos',
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#0B1F3A] pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20panoramic%20view%20Dominican%20Republic%20Punta%20Cana%20airport%20from%20above%2C%20multiple%20aircraft%20on%20tarmac%2C%20tropical%20Caribbean%20landscape%2C%20lush%20green%2C%20turquoise%20ocean%20in%20distance%2C%20golden%20hour%20light&width=1920&height=600&seq=airports-hero-1&orientation=landscape"
            alt="Aeropuertos República Dominicana"
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/80 to-[#0B1F3A]" />
        </div>
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-blue-400 text-sm hover:text-blue-300 cursor-pointer">Inicio</Link>
            <i className="ri-arrow-right-s-line text-blue-600" />
            <span className="text-slate-400 text-sm">Aeropuertos</span>
          </div>
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <span className="text-3xl">🛬</span>
            <div>
              <h1 className="text-white font-bold leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)" }}>
                AEROPUERTOS
              </h1>
              <p className="text-blue-200/70 text-sm mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Infraestructura aeroportuaria · Noticias y datos del sector
              </p>
            </div>
          </div>

          {/* Search bar + Category tags */}
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
            <form onSubmit={handleSearch} className="relative max-w-md flex-shrink-0">
              <div className="relative flex items-center bg-white/10 border border-white/20 rounded-xl backdrop-blur-sm">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-blue-200/60 text-sm" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar noticias de aeropuertos..."
                  className="w-full pl-9 pr-24 py-3 bg-transparent text-sm text-white placeholder-blue-200/40 focus:outline-none"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                />
                <button
                  type="submit"
                  className="absolute right-2 px-4 py-1.5 bg-white hover:bg-blue-50 text-[#0B1F3A] text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Buscar
                </button>
              </div>
            </form>

            {/* Category filter tags */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-1.5 text-xs font-inter font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat
                      ? "bg-blue-600 text-white"
                      : "bg-white/10 text-blue-200/80 hover:bg-white/20 border border-white/10"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <AdCintillo placement="Aeropuertos" className="bg-[#F4F7FB]" />

      {/* Noticias de aeropuertos - filtered by category */}
      <AeropuertosGrid categoryFilter={activeCategory} />

      {/* Aeropuertos Dominicanos */}
      <section className="max-w-screen-xl mx-auto px-4 md:px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-1 h-7 bg-red-600 rounded-full" />
          <h2 className="text-[#0B1F3A] font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.6rem" }}>
            AEROPUERTOS DOMINICANOS
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {aeropuertosRD.map((aero) => (
            <a
              key={aero.iata}
              href={aero.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white rounded-2xl border border-slate-100 hover:border-red-200 transition-all hover:-translate-y-0.5 overflow-hidden cursor-pointer block"
            >
              <div className="aspect-[16/10] w-full overflow-hidden">
                <img
                  src={aero.imagen}
                  alt={aero.nombre}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <span
                    className="inline-block px-3 py-1 bg-[#0B1F3A] text-white text-xs font-bold rounded-lg tracking-wider"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    {aero.iata}
                  </span>
                  <div className="w-8 h-8 flex items-center justify-center bg-slate-50 rounded-lg group-hover:bg-red-50 transition-colors">
                    <i className="ri-external-link-line text-slate-400 group-hover:text-red-500 transition-colors" />
                  </div>
                </div>
                <h3
                  className="text-[#0B1F3A] font-bold text-sm leading-snug mb-1"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  {aero.nombre}
                </h3>
                <p className="text-slate-400 text-xs" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {aero.ciudad}, {aero.provincia}
                </p>
              </div>
            </a>
          ))}
        </div>
      </section>

      <NewsletterSection />
      <NewsletterPopup />
      <Footer />
    </main>
  );
}